import CategoriesEntity from '@/entities/api/categories/category'
import { Entities } from '@/entities/api/entities'
import Category from '@/pages/category'
import { entityToSpanish } from '@/utils/entity-convert'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
	Menu,
	X,
	ChevronDown,
	Pill,
	Shirt,
	Target,
	Truck,
	Tag,
	Flame,
	Sparkles,
	Package,
} from 'lucide-react'

interface Subcategory {
	id: string
	name: string
	type: Entities
	count?: number
	isNew?: boolean
	isHot?: boolean
}

interface Category {
	id: Entities
	name: string
	icon: React.ComponentType<any>
	subcategories: Subcategory[]
	isExpanded: boolean
	badge?: string
}

interface CategoryResponse {
	name: string
	icon: React.ComponentType<any>
}

const initialCategories: Category[] = [
	{
		id: Entities.SUPPLEMENTS,
		name: 'Suplementación',
		icon: Pill,
		badge: 'HOT',
		subcategories: [
			{
				id: 'proteins',
				name: 'Proteínas',
				count: 45,
				type: Entities.SUPPLEMENTS,
				isHot: true,
			},
		],
		isExpanded: false,
	},
	// {
	// 	id: Entities.CLOTHES,
	// 	name: 'Ropa Deportiva',
	// 	icon: Shirt,
	// 	badge: 'NEW',
	// 	subcategories: [
	// 		{
	// 			id: 'shirts',
	// 			name: 'Camisetas',
	// 			type: Entities.CLOTHES,
	// 			count: 89,
	// 			isNew: true,
	// 		},
	// 	],
	// 	isExpanded: false,
	// },
	{
		id: Entities.PLANS_PERSONALIZED,
		name: 'Entrenamiento',
		icon: Target,
		subcategories: [
			{
				id: 'basic-plans',
				name: 'Planes Básicos',
				type: Entities.PLANS_PERSONALIZED,
				count: 12,
			},
		],
		isExpanded: false,
	},
	// {
	// 	id: Entities.ACCESSORIES,
	// 	name: 'Accesorios Fitness',
	// 	icon: Dumbbell,
	// 	subcategories: [
	// 		{
	// 			id: 'weights',
	// 			name: 'Pesas',
	// 			count: 156,
	// 			type: Entities.ACCESSORIES,
	// 			isHot: true,
	// 		},
	// 	],
	// 	isExpanded: false,
	// },
]

const getCategoryType = (type: Entities): CategoryResponse => {
	switch (type) {
		case Entities.SUPPLEMENTS:
			return {
				name: 'Suplementación',
				icon: Pill,
			}
		case Entities.CLOTHES:
			return {
				name: 'Ropa Deportiva',
				icon: Shirt,
			}
		// case Entities.PLANS_PERSONALIZED:
		// 	return {
		// 		name: 'Entrenamiento',
		// 		icon: Target,
		// 	}
		// case Entities.ACCESSORIES:
		// 	return {
		// 		name: 'Accesorios Fitness',
		// 		icon: Dumbbell,
		// 	}
		default:
			return {
				name: 'Categoría desconocida',
				icon: Package,
			}
	}
}

const MenuCategories = ({ categories }: { categories: CategoriesEntity[] }) => {
	const [categoriesData, setCategoriesData] =
		useState<Category[]>(initialCategories)
	useEffect(() => {
		if (!categories || categories.length === 0) return

		const grouped = categories.reduce((acc, curr) => {
			if (!acc[curr.type]) {
				acc[curr.type] = []
			}
			acc[curr.type].push(curr)
			return acc
		}, {} as Record<Entities, CategoriesEntity[]>)

		const allowedTypes = [
			Entities.SUPPLEMENTS,
			Entities.PLANS_PERSONALIZED,
		]
		
		const categoriesMap: Category[] = Object.entries(grouped)
			.filter(([type]) => allowedTypes.includes(type as Entities))
			.map(([type, entries]) => {
				const { name, icon } = getCategoryType(type as Entities)
		
				const subcategories: Subcategory[] = entries.map((entry, index) => ({
					id: entry._id ?? entry.name.toLowerCase().replace(/\s+/g, '-'),
					name: entry.name,
					type: entry.type,
					count: 99,
					isHot: index === 0,
				}))
		
				const badge = 'HOT'
		
				return {
					id: type as Entities,
					name,
					icon,
					subcategories,
					isExpanded: false,
					badge,
				}
			})

		setCategoriesData(categoriesMap)
	}, [categories])

	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

	const toggleCategory = (categoryId: string): void => {
		setCategoriesData((prev) =>
			prev.map((cat) =>
				cat.id === categoryId
					? { ...cat, isExpanded: !cat.isExpanded }
					: { ...cat, isExpanded: false }
			)
		)
	}

	const toggleMobileMenu = (): void => {
		setIsMenuOpen(!isMenuOpen)
	}

	return (
		<>
			<button
				onClick={toggleMobileMenu}
				className={`lg:hidden fixed top-20 left-4 z-[9999] bg-white/95 backdrop-blur-lg text-gray-800 p-3.5 rounded-2xl shadow-xl border border-gray-200/60 hover:bg-white hover:shadow-2xl hover:shadow-red-500/10 hover:border-red-300/50 transition-all duration-300 transform hover:scale-105 active:scale-95 ${
					isMenuOpen
						? 'opacity-0 invisible pointer-events-none'
						: 'opacity-100 visible'
				}`}
				aria-label='Abrir menú de categorías'
				style={{
					position: 'fixed',
					willChange: 'transform, opacity',
				}}
			>
				<div className='relative'>
					<Menu className='w-6 h-6 text-gray-700' strokeWidth={2.5} />
					<div className='absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-tr from-red-500 to-red-600 rounded-full border-2 border-white shadow-sm'></div>
				</div>

				<div className='absolute left-full ml-3 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-xs px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none shadow-lg'>
					Categorías
				</div>
			</button>

			{isMenuOpen && (
				<div
					className='lg:hidden fixed inset-0 bg-black/50 z-[9990] backdrop-blur-sm transition-opacity duration-300'
					onClick={toggleMobileMenu}
				/>
			)}

			<div
				className={`
				fixed lg:relative top-0 left-0 h-full lg:h-auto w-80 lg:w-1/4 xl:w-1/5 
				bg-gray-50 lg:bg-gray-50 rounded-none lg:rounded-2xl 
				lg:mr-6 p-6 lg:p-6 overflow-y-auto shadow-2xl lg:shadow-xl 
				border-r lg:border border-gray-200/60 z-[9995] lg:z-auto
				transform transition-transform duration-300 ease-in-out lg:transform-none
				${isMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
			`}
			>
				<button
					onClick={toggleMobileMenu}
					className='lg:hidden absolute top-5 right-5 text-gray-500 hover:text-red-600 hover:bg-red-50 p-2.5 rounded-xl transition-all duration-200 z-10 shadow-md border border-gray-200'
					aria-label='Cerrar menú'
				>
					<X className='w-5 h-5' strokeWidth={2} />
				</button>

				<div className='mb-6 lg:mb-6 pb-4 border-b border-gray-200/80'>
					<h3 className='text-xl lg:text-xl font-black text-gray-900 flex items-center'>
						<Package
							className='mr-3 text-red-600 w-6 h-6 lg:w-6 lg:h-6'
							strokeWidth={2.5}
						/>
						CATEGORÍAS
					</h3>
					<p className='text-sm lg:text-sm text-gray-600 mt-1 font-medium'>
						Encuentra lo que buscas
					</p>
				</div>

				<div className='space-y-2 lg:space-y-3'>
					{categoriesData.map((category) => {
						const IconComponent = category.icon
						return (
							<div
								key={category.id}
								className='border border-gray-200/80 rounded-2xl overflow-hidden hover:shadow-lg hover:border-gray-300/60 transition-all duration-300 bg-white'
							>
								<button
									onClick={() => toggleCategory(category.id)}
									className='w-full text-left p-3 lg:p-4 hover:bg-gray-50/80 transition-all duration-300 flex items-center justify-between group'
								>
									<div className='flex items-center space-x-3'>
										<div className='w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center group-hover:from-red-50 group-hover:to-red-100 transition-all duration-300'>
											<IconComponent
												className='w-4 h-4 lg:w-5 lg:h-5 text-gray-600 group-hover:text-red-600 transition-colors duration-300'
												strokeWidth={2}
											/>
										</div>
										<div className='flex-1 min-w-0'>
											<span className='font-bold text-gray-800 group-hover:text-red-600 transition-colors duration-300 text-sm lg:text-sm block truncate'>
												{category.name}
											</span>
											{category.badge && (
												<div className='flex items-center mt-1'>
													<span
														className={`inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full ${
															category.badge === 'HOT'
																? 'bg-red-100 text-red-700 border border-red-200'
																: category.badge === 'NEW'
																? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
																: category.badge === 'SALE'
																? 'bg-orange-100 text-orange-700 border border-orange-200'
																: 'bg-gray-100 text-gray-700 border border-gray-200'
														}`}
													>
														{category.badge === 'HOT' && (
															<Flame className='w-2.5 h-2.5 mr-1' />
														)}
														{category.badge === 'NEW' && (
															<Sparkles className='w-2.5 h-2.5 mr-1' />
														)}
														{category.badge}
													</span>
												</div>
											)}
										</div>
									</div>

									<ChevronDown
										className={`w-4 h-4 lg:w-5 lg:h-5 text-gray-500 transform transition-transform duration-300 flex-shrink-0 ${
											category.isExpanded ? 'rotate-180' : ''
										}`}
										strokeWidth={2}
									/>
								</button>

								{category.isExpanded && (
									<div className='bg-gray-50/60 border-t border-gray-200/60 max-h-52 overflow-y-auto'>
										{category.subcategories.map((sub) => (
											<Link
												to={`/SubCategoria/${entityToSpanish(
													sub.type
												)}/${sub.name.replace(/ /g, '-')}`}
												key={sub.id}
												className='w-full text-left py-3 lg:py-3 px-4 lg:px-5 text-sm lg:text-sm text-gray-700 hover:text-red-600 hover:bg-white/80 transition-all duration-300 flex items-center justify-between group border-b border-gray-200/40 last:border-b-0'
											>
												<div className='flex items-center space-x-2 flex-1 min-w-0'>
													<div className='w-1.5 h-1.5 bg-gray-400 rounded-full group-hover:bg-red-600 transition-colors duration-300 flex-shrink-0'></div>
													<span className='group-hover:font-semibold transition-all duration-300 truncate'>
														{sub.name}
													</span>
													<div className='flex items-center space-x-1.5 flex-shrink-0'>
														{sub.isNew && (
															<span className='bg-emerald-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold border border-emerald-600'>
																NEW
															</span>
														)}
														{sub.isHot && (
															<span className='bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold border border-red-600 flex items-center'>
																<Flame className='w-2.5 h-2.5' />
															</span>
														)}
													</div>
												</div>

												{sub.count && (
													<span className='text-xs lg:text-xs text-gray-500 group-hover:text-red-500 font-semibold flex-shrink-0 ml-2 bg-gray-100 group-hover:bg-red-50 px-1.5 py-0.5 rounded-lg transition-all duration-300'>
														{sub.count}
													</span>
												)}
											</Link>
										))}
									</div>
								)}
							</div>
						)
					})}
				</div>

				<div className='mt-6 lg:mt-6 p-4 lg:p-5 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-2xl text-white shadow-xl border border-emerald-400/30'>
					<div className='flex items-center mb-2'>
						<Truck className='w-5 h-5 mr-2' strokeWidth={2} />
						<div className='text-base lg:text-lg font-black'>ENVÍO GRATIS</div>
					</div>
					<div className='text-sm lg:text-sm opacity-95 mb-3 font-medium'>
						En compras superiores a $120.000
					</div>
					<Link
						to='/Promociones'
						className='w-full cursor-pointer bg-white text-emerald-700 px-4 py-2 lg:px-4 lg:py-2 rounded-xl text-sm lg:text-sm font-bold hover:bg-emerald-50 hover:text-emerald-800 transition-all duration-300 shadow-lg border border-emerald-200 flex items-center justify-center'
					>
						<Tag className='w-4 h-4 mr-2' strokeWidth={2.5} />
						VER PROMOCIONES
					</Link>
				</div>

				<div className='mt-5 lg:mt-5 p-3 lg:p-4 bg-white rounded-2xl border border-gray-200/80 shadow-md'>
					<div className='grid grid-cols-2 gap-3 lg:gap-4 text-center'>
						<div className='p-2 bg-gray-50 rounded-xl'>
							<div className='text-lg lg:text-xl font-black text-gray-800 mb-0.5'>
								500+
							</div>
							<div className='text-xs lg:text-xs text-gray-600 font-medium'>
								Productos
							</div>
						</div>
						<div className='p-2 bg-gray-50 rounded-xl'>
							<div className='text-lg lg:text-xl font-black text-gray-800 mb-0.5'>
								24/7
							</div>
							<div className='text-xs lg:text-xs text-gray-600 font-medium'>
								Soporte
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default MenuCategories
